import { GROUP_SELECTION } from 'pxc-editor-core/constants';
import {
  INTERACTION_MODE,
  ARIAL_FONT,
  LIBERATION_SANS_FONT,
  LIBERATION_SERIF_FONT,
  LIBERATION_MONO_FONT,
} from 'pxc-editor-material-svg-rendering/constants';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import {
  getColorByVariantPaperColor,
  getColorByVariantRibbonColor,
} from 'pxc-editor-core/helpers/material';
import envReducer, {
  initState as envInitState,
} from 'EnvironmentModule/store/reducers';
import {
  SET_MODE,
  SET_PRESELECTED_PRINTER,
  EDIT_SECTION,
  MOVE_SECTION,
  DELETE_SECTION,
  DELETE_SECTIONS,
  DELETE_SELECTED_SECTIONS,
  ADD_PAGE,
  DELETE_PAGE,
  OVERRIDE_PAGE,
  OVERRIDE_PAGES,
  DELETE_PAGE_CONTENT,
  SET_CELL_SELECTION_TYPE,
  SET_GROUP_SELECTION_TYPE,
  ADD_SHIELD_TO_SELECTION,
  CLEAR_SECTIONS_SELECTION,
  CLEAR_SELECTED_SECTIONS,
  CLEAR_ALL_SELECTED_SECTIONS,
  CLEAR_SHIELDS_SELECTION,
  REMOVE_SHIELD_FROM_SELECTION,
  REMOVE_SECTION_FROM_SELECTION,
  ADD_FIRST_PAGE,
  CLEAR_PAGES_SELECTION,
  SET_EDITOR_MENU_TAB,
  SET_COLORED_STATE,
  SET_SECTIONS_DISPLAY_STATE,
  SET_GRID_DISPLAY_STATE,
  SET_GRID_WIDTH,
  SET_INPUT_DISPLAY_STATE,
  SET_TEXT_TYPING_RESOURCE,
  SET_VIEWBOX_X,
  SET_VIEWBOX_Y,
  SET_VIEWBOX_DX,
  SET_VIEWBOX_DY,
  TOGGLE_ZOOM_STATE,
  SET_SCALE_COEF,
  SET_PAGINATION_PAGE_NUM,
  UNDO_STEP,
  REDO_STEP,
  BULK_SHAPE_SELECTION,
  CLEAR_GROUP_SELECTION,
  ADD_SECTION,
  ADD_SECTIONS,
  ADD_NEW_SECTIONS,
  CREATE_SNAPSHOT,
  COPY_SEL_WIZARD_CONTENT,
  CMME_STATES,
  ADD_CUSTOM_FONT_COLOR,
  ADD_CUSTOM_SECTION_BORDER_COLOR,
  ADD_CUSTOM_SECTION_FILL_COLOR,
  ADD_CUSTOM_SYMBOL_STROKE_COLOR,
  ADD_CUSTOM_SYMBOL_FILL_COLOR,
  SET_TARGET_DEVICE,
  SET_TEMPORARY_VIEWBOX_X,
  SET_TEMPORARY_VIEWBOX_Y,
  LOAD_PROJECT_FILE,
  SET_PROJECT_NAME,
  SINGLE_CELLS,
  SELECTION_TOOL,
  ZOOM_OUT_STATE,
  RESET_STATE,
  SET_LOCALE,
  SET_LAST_SAVED_PAGES,
  SET_PAGE_RESIZE_TYPE,
  SET_PAGE_LENGTH,
  SET_COLOR_SCHEMA,
  SET_RIBBON_COLOR,
  SET_PAPER_COLOR,
  SET_MULTI_COLORED_STATE,
  SET_SHOW_LOADER_STATE,
  SET_CONTENT_PLACING_DEBUGGING_STATE,
  SET_DEBUGGING_GRID_STATE,
  SET_FROM_WIZARD_STATE,
  SET_WIZARD_DATA,
  SET_IS_CABLE_FLAG_SYNC,
  SET_USER_SIZE_UNIT,
  USER_SIZE_UNIT_MM,
  ADD_PAGE_ROTATION,
  ADD_PAGE_MIRROR_X,
  ADD_PAGE_MIRROR_Y,
  SWITCH_SELECTED_PAGE,
  SWITCH_SELECTED_SECTION,
  SWITCH_SELECTED_SHIELD,
  SET_SNACKBAR_STATE,
  SET_USER_DEVICE_MANIPULATOR_TYPE,
  USER_DEVICE_MANIPULATOR_TYPE,
  SET_OTHER_CONTENT_STATE,
  SET_AUTOFILL_AND_COPPY_PATTERN,
  SET_FICTIVE_SECTIONS,
  ADD_SECTION_TO_MATRIX_SELECTION,
  REMOVE_SECTION_FROM_MATRIX_SELECTION,
  SET_AUTOFILL_MATRIX_TEMPLATE,
  SET_FONT_COEF,
  SET_SHOW_INSERT_MODAL_STATE,
  SET_LASER_OUTPUT_QUALITY,
  XCORE_ENVIRONMENT,
  SET_PLATFORM_ENVIRONMENT,
  SET_FONT_SIZE_COEF,
  SET_LINE_HEIGHT_COEF,
  SET_LETTER_SPACING_COEF,
  NO_PRINTER_ID,
  SET_PRINTER_LIST,
  SET_VARIANT_COLOR_MODAL_OPEN,
  SET_FLOATABLE_INPUT_VISIBLE,
  SET_PRINTING,
  ADD_SELECTED_SECTIONS,
  SPLIT_SECTIONS,
  REMOVE_BLUR_FROM_ALL_INPUTS,
} from '@/constants';
import { isTouchDevice } from '@/helpers/common';
import { getSnapshotFromState, getStateFromSnapshot } from '@/helpers';

const initState = Object.assign({}, envInitState, {
  blurAllInputs: false,
  locale: 'en',
  pages: {},
  pagesOrder: [],
  mode: INTERACTION_MODE.EDIT,
  fillMode: null,
  editorMenuTab: null,
  colorSchema: null,
  projectName: null,
  cellSelectionType: SINGLE_CELLS,
  groupSelectionType: [],
  pagesSelection: [],
  shieldsSelection: {},
  fictiveSections: {},
  fictiveSectionsSelection: {},
  sectionsSelection: {}, // store content of section before changes
  isColored: true,
  isMultiColored: true,
  areSectionsHidden: false,
  isGridDisplayed: false,
  gridWidth: 1.2,
  isInputDisplayed: true,
  textTypingResource: null,
  temporaryViewboxX: null,
  temporaryViewboxY: null,
  viewboxX: 0,
  viewboxY: 0,
  viewboxDx: 0,
  viewboxDy: 0,
  zoomState: ZOOM_OUT_STATE,
  scaleCoef: 1, // px per mm
  zoomOutScaleCoef: 1,
  zoomInScaleCoef: 1,
  paginationPageNum: 1,
  fontCoef: 0.1,
  lastSavedPages: {},
  snapshots: [], // TODO: move to store in cache as strings
  snapshotPointer: -1,
  isUndoRedoStateChanged: false,
  copiedSection: null,
  copiedShield: null,
  copiedSections: [],
  customFontColors: [],
  customSectionBorderColors: [],
  customSectionFillColors: [],
  customSymbolStrokeColors: [],
  customSymbolFillColors: [],
  ribbonColor: getColorByVariantRibbonColor(),
  paperColor: getColorByVariantPaperColor(),
  targetDevice: null,
  isShowLoader: false,
  isContentPlacingDebugging: false,
  isDebuggingGrid: false,
  userSizeUnit: USER_SIZE_UNIT_MM,
  isFromWizard: false,
  isShowSnackbar: {
    intlId: '',
    defaultMessage: 'Success!',
    message: '',
    variant: '',
    show: false,
    autoHideDuration: null,
  },
  userDeviceManipulatorType: isTouchDevice()
    ? USER_DEVICE_MANIPULATOR_TYPE.TOUCH
    : USER_DEVICE_MANIPULATOR_TYPE.MOUSE,
  includeOtherContent: false, // use on autofill. means if need to copy all sections of start shield to next filled shields
  autoFillAndCopyPattern: null,
  autoFillMatrixTemplate: null,
  autoFillMatrixStyleTemplate: null,
  preselectedPrinter: NO_PRINTER_ID,
  printerList: [],
  selWizard: null,
  copiedSelWizard: null,
  isShowInsertModal: false,
  cmmeStates: {
    pages: {},
    sideBarStates: {
      VIEW: true,
      TEXT: false,
      ALIGNMENT: false,
      SECTIONS: false,
      ROTATEFLIP: false,
      MATRIX: false,
      SEPARATOR: false,
      SHAPERECTANGLE: true,
      SHAPEELLIPSE: true,
      LASER: false,
      SYMBOL: true,
      GRAPHIC: false,
    },
  },
  laserOutputQuality: '',
  platform: {
    config: {
      environment: XCORE_ENVIRONMENT.MARKING_SYSTEM,
    },
  },
  fontSizeCoef: {
    [ARIAL_FONT]: 1,
    [LIBERATION_SANS_FONT]: 1,
    [LIBERATION_SERIF_FONT]: 1,
    [LIBERATION_MONO_FONT]: 1,
  },
  lineHeightCoef: 1.13,
  letterSpacingCoef: 0,
  isVariantColorModalOpen: false,
  floatableInputVisible: false,
  isPrinting: false,
});

// isUndoRedoStateChanged: part of strategy of undo/redo functionality
// general problem of undo/redo - 'blank' steps - snapshots, that don't have changes one by one
// to solve this issue we need to implement some 'changes detection' strategy
// IMPORTANT: important changes related only to changes of 'page' or 'section',
// but for correct restoring of state we need to use more variables(list in getSnapshotFromState)
// current strategy implementation - isUndoRedoStateChanged - setting this variable on changes
// previous implementation - compare snapshots - wrong, because snapshot contain variables not important to detect changes

export default function(state = initState, action) {
  const cmmeStates = state.cmmeStates ? state.cmmeStates : { pages: {} };
  const cmmeStatePages = state.cmmeStates?.pages ? state.cmmeStates.pages : {};
  const preselectedPageID = state.pagesSelection[0];

  switch (action.type) {
    case SET_CONTENT_PLACING_DEBUGGING_STATE:
      return { ...state, isContentPlacingDebugging: action.payload };
    case SET_DEBUGGING_GRID_STATE:
      return { ...state, isDebuggingGrid: action.payload };
    case SET_FROM_WIZARD_STATE:
      return { ...state, isFromWizard: action.payload };
    case SET_WIZARD_DATA:
      return { ...state, selWizard: action.payload };
    case SET_IS_CABLE_FLAG_SYNC:
      const selWizard = state.selWizard;
      if (selWizard?.data) {
        selWizard.data.isCableFlagSync = action.payload === true;
      }
      return { ...state, selWizard: selWizard };
    case SET_SHOW_INSERT_MODAL_STATE:
      return { ...state, isShowInsertModal: action.payload };
    case SET_RIBBON_COLOR:
      return { ...state, ribbonColor: action.payload };
    case SET_PAPER_COLOR:
      return { ...state, paperColor: action.payload };
    case SET_COLOR_SCHEMA:
      return { ...state, colorSchema: action.payload };
    case SET_LAST_SAVED_PAGES:
      return { ...state, lastSavedPages: action.payload };
    case SET_PAGE_LENGTH: {
      const { page } = action.payload;

      return {
        ...state,
        pages: { ...state.pages, [page.uid]: page },
        isUndoRedoStateChanged: true,
      };
    }
    case SET_PAGE_RESIZE_TYPE: {
      const { pages } = state;
      const { type, pageId } = action.payload;

      return {
        ...state,
        pages: { ...pages, [pageId]: { ...pages[pageId], resizeType: type } },
        isUndoRedoStateChanged: true,
      };
    }
    case SET_LOCALE:
      return { ...state, locale: action.payload };
    case REMOVE_BLUR_FROM_ALL_INPUTS:
      return { ...state, blurAllInputs: action.payload };
    case RESET_STATE:
      return {
        ...initState,
        targetDevice: state.targetDevice,
        userDeviceManipulatorType: state.userDeviceManipulatorType,
        locale: state.locale,
        userSizeUnit: state.userSizeUnit,
        isShowLoader: state.isShowLoader,
        platform: state.platform,
        fontSizeCoef: state.fontSizeCoef,
        cmmeStates: state.cmmeStates,
      };
    case SET_PROJECT_NAME:
      state.snapshotPointer = -1;
      state.snapshots = [];
      state.isUndoRedoStateChanged = false;
      return { ...state, projectName: action.payload };
    case LOAD_PROJECT_FILE:
      return {
        ...state,
        ...action.payload,
      };
    case SET_TARGET_DEVICE:
      return { ...state, targetDevice: action.payload };
    case 'COPY&PASTE/SET_COPIED_SECTIONS': {
      const {
        copiedShield = null,
        copiedSection = null,
        copiedSections = [],
      } = action.payload;
      return { ...state, copiedShield, copiedSection, copiedSections };
    }
    case COPY_SEL_WIZARD_CONTENT: {
      return {
        ...state,
        copiedSelWizard: action.payload,
      };
    }
    case CMME_STATES: {
      return {
        ...state,
        cmmeStates: action.payload,
      };
    }
    case ADD_CUSTOM_FONT_COLOR:
      return {
        ...state,
        customFontColors: [...state.customFontColors, action.payload],
      };
    case ADD_CUSTOM_SECTION_BORDER_COLOR:
      return {
        ...state,
        customSectionBorderColors: [
          ...state.customSectionBorderColors,
          action.payload,
        ],
      };
    case ADD_CUSTOM_SECTION_FILL_COLOR:
      return {
        ...state,
        customSectionFillColors: [
          ...state.customSectionFillColors,
          action.payload,
        ],
      };
    case ADD_CUSTOM_SYMBOL_STROKE_COLOR:
      return {
        ...state,
        customSymbolStrokeColors: [
          ...state.customSymbolStrokeColors,
          action.payload,
        ],
      };
    case ADD_CUSTOM_SYMBOL_FILL_COLOR:
      return {
        ...state,
        customSymbolFillColors: [
          ...state.customSymbolFillColors,
          action.payload,
        ],
      };
    case CREATE_SNAPSHOT: {
      const { snapshotPointer, snapshots, isUndoRedoStateChanged } = state;
      // remove other snapshots from current point. for case if was moving back in snapshots history
      const newSnapshots = snapshots.slice(0, snapshotPointer + 1);
      let newSnapshotPointer = snapshotPointer;
      if (isUndoRedoStateChanged || snapshotPointer < 0) {
        const snapshot = getSnapshotFromState(state);
        newSnapshots.push(snapshot);
        newSnapshotPointer++; // todo: add max val validation
      }
      const newState = {
        ...state,
        snapshots: newSnapshots,
        snapshotPointer: newSnapshotPointer,
        isUndoRedoStateChanged: false,
      };
      return newState;
    }
    case UNDO_STEP: {
      const { snapshotPointer, snapshots, isUndoRedoStateChanged } = state;
      let newState = state;
      if (snapshotPointer >= 0) {
        let prevState = snapshots[snapshotPointer];
        const newSnapshots = snapshots;
        let newSnapshotPointer = snapshotPointer;
        const lastSnapshotPointer = newSnapshots.length - 1;
        if (snapshotPointer === lastSnapshotPointer) {
          // add new snapshot if it first back in history, so we can return to latest state
          if (isUndoRedoStateChanged) {
            newSnapshots.push(getSnapshotFromState(state));
          } else {
            newSnapshotPointer--;
            prevState = snapshots[newSnapshotPointer];
          }
        } else {
          newSnapshotPointer--;
          prevState = snapshots[newSnapshotPointer];
        }
        if (newSnapshotPointer === 0) {
          // correct handling of first snapshot
          newSnapshotPointer--;
        }
        const snapshot = prevState ? getStateFromSnapshot(prevState) : null;
        newState = {
          ...state,
          ...snapshot,
          snapshotPointer: newSnapshotPointer,
          snapshots: newSnapshots,
          isUndoRedoStateChanged: false,
        };
      }

      return newState;
    }
    case REDO_STEP: {
      const { snapshotPointer, snapshots } = state;
      let newSnapshotPointer = snapshotPointer + 1;
      if (snapshotPointer === -1) {
        // correct handling of first snapshot
        newSnapshotPointer++;
      }
      let nextState = snapshots[newSnapshotPointer];
      const newState = nextState
        ? {
            ...state,
            ...getStateFromSnapshot(nextState),
            snapshotPointer: newSnapshotPointer,
            isUndoRedoStateChanged: false,
          }
        : state;

      return newState;
    }
    case SET_FONT_COEF:
      return { ...state, fontCoef: action.payload };
    case SET_SCALE_COEF:
      return { ...state, scaleCoef: action.payload };
    case TOGGLE_ZOOM_STATE:
      return { ...state, zoomState: action.payload };
    case SET_PAGINATION_PAGE_NUM:
      return { ...state, paginationPageNum: action.payload };
    case SET_VIEWBOX_DX:
      return { ...state, viewboxDx: action.payload };
    case SET_VIEWBOX_DY:
      return { ...state, viewboxDy: action.payload };
    case SET_VIEWBOX_X:
      return { ...state, viewboxX: action.payload };
    case SET_VIEWBOX_Y:
      return { ...state, viewboxY: action.payload };
    case SET_TEMPORARY_VIEWBOX_X:
      return { ...state, temporaryViewboxX: action.payload };
    case SET_TEMPORARY_VIEWBOX_Y:
      return { ...state, temporaryViewboxY: action.payload };
    case SET_INPUT_DISPLAY_STATE:
      return { ...state, isInputDisplayed: action.payload };
    case SET_TEXT_TYPING_RESOURCE:
      return { ...state, textTypingResource: action.payload };
    case SET_GRID_WIDTH:
      return { ...state, gridWidth: action.payload };
    case SET_GRID_DISPLAY_STATE:
      return { ...state, isGridDisplayed: action.payload };
    case SET_SECTIONS_DISPLAY_STATE:
      return { ...state, areSectionsHidden: action.payload };
    case SET_COLORED_STATE:
      return { ...state, isColored: action.payload };
    case SET_MULTI_COLORED_STATE:
      return { ...state, isMultiColored: action.payload };
    case SET_SHOW_LOADER_STATE:
      return { ...state, isShowLoader: action.payload };
    case SET_MODE:
      return { ...state, mode: action.payload };
    case SET_PRESELECTED_PRINTER:
      return { ...state, preselectedPrinter: action.payload };
    case SET_PRINTER_LIST:
      return { ...state, printerList: action.payload };
    case SET_EDITOR_MENU_TAB:
      return { ...state, editorMenuTab: action.payload };
    case CLEAR_GROUP_SELECTION:
      return { ...state, groupSelectionType: [] };
    case SET_CELL_SELECTION_TYPE:
      switch (action.payload) {
        case SINGLE_CELLS:
          return {
            ...state,
            groupSelectionType: [],
            cellSelectionType: action.payload,
          };
        case SELECTION_TOOL:
          return {
            ...state,
            groupSelectionType: [GROUP_SELECTION.ALL_GROUPS],
            cellSelectionType: action.payload,
          };
        default:
          return state;
      }
    case SET_GROUP_SELECTION_TYPE: {
      const {
        groupSelectionType,
        sectionsSelection,
        shieldsSelection,
      } = action.payload;

      return {
        ...state,
        groupSelectionType,
        sectionsSelection,
        shieldsSelection,
      };
    }
    case ADD_FIRST_PAGE: {
      const newPages = { [action.payload.uid]: action.payload };
      if (state.selWizard?.application_type === 'CABLE_FLAG') {
        state.selWizard.data.pages = {
          ...state.selWizard.data.pages,
          [action.payload.uid]: {
            flagLength: state.selWizard.data.flagLength,
          },
        };
      }
      cmmeStatePages[action.payload.uid] = {
        ...cmmeStatePages[action.payload.uid],
        selectedSection: undefined,
        shieldsSelection: state.shieldsSelection,
        sectionsSelection: {},
      };
      cmmeStates['pages'] = cmmeStatePages;

      return {
        ...state,
        cmmeStates: cmmeStates,
        pages: newPages,
        pagesOrder: [action.payload.uid],
        snapshots: initState.snapshots,
        snapshotPointer: initState.snapshotPointer,
        isUndoRedoStateChanged: false,
        selWizard: state.selWizard,
      };
    }
    case ADD_PAGE: {
      const { page: pageToAdd } = action.payload;
      if (state.selWizard?.application_type === 'CABLE_FLAG') {
        state.selWizard.data.pages = {
          ...state.selWizard.data.pages,
          [pageToAdd.uid]: {
            flagLength: state.selWizard.data.flagLength,
          },
        };
      }
      return {
        ...state,
        pages: { ...state.pages, [pageToAdd.uid]: pageToAdd },
        pagesOrder: [...state.pagesOrder, pageToAdd.uid],
        isUndoRedoStateChanged: true,
        selWizard: state.selWizard,
      };
    }
    case OVERRIDE_PAGE: {
      const {
        page,
        shieldsSelection = state.shieldsSelection,
        sectionsSelection = state.sectionsSelection,
        fictiveSections = state.fictiveSections,
        fictiveSectionsSelection = {},
      } = action.payload;

      return {
        ...state,
        pages: { ...state.pages, [page.uid]: page },
        shieldsSelection,
        sectionsSelection,
        fictiveSections,
        fictiveSectionsSelection,
        isUndoRedoStateChanged: true,
      };
    }
    case OVERRIDE_PAGES: {
      const {
        pages,
        pagesOrder,
        sectionsSelection = state.sectionsSelection,
        shieldsSelection = state.shieldsSelection,
        paginationPageNum = state.paginationPageNum,
        pagesSelection = state.pagesSelection,
      } = action.payload;

      return {
        ...state,
        pages,
        pagesOrder,
        fictiveSectionsSelection: {},
        sectionsSelection,
        shieldsSelection,
        paginationPageNum,
        pagesSelection,
        isUndoRedoStateChanged: true,
      };
    }
    case SWITCH_SELECTED_PAGE:
      if (
        state?.pages &&
        state?.selWizard?.application_type === 'CABLE_FLAG' &&
        state?.selWizard?.data?.pages &&
        state?.selWizard?.data?.isFlagLengthAuto
      ) {
        state.selWizard.data.flagLength =
          state.selWizard.data.pages[action.payload]?.flagLength ||
          state.selWizard.data.flagLength ||
          7;
      }
      return {
        ...state,
        pagesSelection: [action.payload],
      };
    case ADD_SHIELD_TO_SELECTION:
      return {
        ...state,
        shieldsSelection: {
          ...state.shieldsSelection,
          [action.payload.id]: action.payload,
        },
      };
    case REMOVE_SHIELD_FROM_SELECTION:
      const shSel = { ...state.shieldsSelection };
      delete shSel[action.payload.id];
      return {
        ...state,
        shieldsSelection: { ...shSel },
        sectionsSelection: initState.sectionsSelection,
        fictiveSectionsSelection: initState.fictiveSectionsSelection,
      };
    case SWITCH_SELECTED_SHIELD:
      return {
        ...state,
        shieldsSelection: { [action.payload.id]: action.payload },
        sectionsSelection: initState.sectionsSelection,
        fictiveSectionsSelection: initState.fictiveSectionsSelection,
      };
    case BULK_SHAPE_SELECTION:
      return { ...state, shieldsSelection: action.payload };
    case DELETE_SECTION:
      const { page } = getAllLayers(action.payload.id, state.pages);
      const updatedSections = { ...page.sections };
      delete updatedSections[action.payload.id];
      const secSel = { ...state.sectionsSelection };
      delete secSel[action.payload.id];
      const updatedPage = { ...page, sections: updatedSections };
      return {
        ...state,
        sectionsSelection: secSel,
        fictiveSectionsSelection: secSel,
        pages: {
          ...state.pages,
          [page.uid]: updatedPage,
        },
        isUndoRedoStateChanged: true,
      };
    case DELETE_SECTIONS: {
      const updatedPage = { ...action.payload, sections: {} };
      return {
        ...state,
        pages: { ...state.pages, [action.payload.uid]: updatedPage },
        isUndoRedoStateChanged: true,
      };
    }
    case DELETE_PAGE_CONTENT: {
      const { page: pageToChange } = getAllLayers(
        action.payload.uid,
        state.pages
      );
      return {
        ...state,
        pages: {
          ...state.pages,
          [pageToChange.uid]: { ...pageToChange, sections: {} },
        },
        isUndoRedoStateChanged: true,
      };
    }
    case DELETE_SELECTED_SECTIONS: {
      // correct name 'OVERRIDE PAGE'
      const {
        page,
        sectionsSelection = state.sectionsSelection,
        shieldsSelection = state.shieldsSelection,
      } = action.payload;
      return {
        ...state,
        pages: { ...state.pages, [page.uid]: { ...page } },
        sectionsSelection,
        shieldsSelection,
        isUndoRedoStateChanged: true,
      };
    }
    case DELETE_PAGE:
      const ps = { ...state.pages };
      delete ps[action.payload.uid];
      if (
        state.selWizard?.application_type === 'CABLE_FLAG' &&
        state.selWizard?.data?.pages
      ) {
        state.selWizard.data.pages[action.payload.uid] = null;
        delete state.selWizard.data.pages[action.payload.uid];
      }
      return {
        ...state,
        pages: ps,
        pagesOrder: state.pagesOrder.filter(
          puid => puid !== action.payload.uid
        ),
        isUndoRedoStateChanged: true,
        selWizard: state.selWizard,
      };
    case EDIT_SECTION:
    case MOVE_SECTION: // correct name 'OVERRIDE SECTION'
      const { page: pageToChange } = getAllLayers(
        action.payload.id,
        state.pages
      );
      const changedSections = {
        ...pageToChange.sections,
        [action.payload.id]: action.payload,
      };
      const changedPage = { ...pageToChange, sections: changedSections };
      return {
        ...state,
        pages: { ...state.pages, [changedPage.uid]: changedPage },
        isUndoRedoStateChanged: true,
      };
    case SWITCH_SELECTED_SECTION: {
      const sectionToSelect = action.payload;
      const { page } = getAllLayers(sectionToSelect.id, state.pages);
      // move selected section to the end of list to render on svg over all other sections

      cmmeStatePages[page.uid] = {
        ...cmmeStatePages[page.uid],
        selectedSection: sectionToSelect.id,
        shieldsSelection: state.shieldsSelection,
        sectionsSelection: {},
      };
      cmmeStates['pages'] = cmmeStatePages;

      return {
        ...state,
        cmmeStates: cmmeStates,
        sectionsSelection: { [sectionToSelect.id]: sectionToSelect },
        fictiveSectionsSelection: { [sectionToSelect.id]: sectionToSelect },
        pages: { ...state.pages, [page.uid]: { ...page } },
      };
    }
    case REMOVE_SECTION_FROM_SELECTION: {
      const secSel = { ...state.sectionsSelection };
      delete secSel[action.payload];
      return {
        ...state,
        sectionsSelection: secSel,
        fictiveSectionsSelection: secSel,
      };
    }
    case CLEAR_SHIELDS_SELECTION:
      return {
        ...state,
        shieldsSelection: initState.shieldsSelection,
        groupSelectionType: initState.groupSelectionType,
      };
    case CLEAR_SECTIONS_SELECTION:
      if (!!preselectedPageID) {
        cmmeStatePages[preselectedPageID] = {
          ...cmmeStatePages[preselectedPageID],
          selectedSection: undefined,
          shieldsSelection: state.shieldsSelection,
          sectionsSelection: {},
        };
        cmmeStates['pages'] = cmmeStatePages;
      }
      return {
        ...state,
        cmmeStates: cmmeStates,
        sectionsSelection: initState.sectionsSelection,
        fictiveSectionsSelection: initState.fictiveSectionsSelection,
      };
    case CLEAR_SELECTED_SECTIONS: {
      return {
        ...state,
        groupSelectionType: initState.groupSelectionType,
        shieldsSelection: initState.shieldsSelection,
        sectionsSelection: initState.sectionsSelection,
        fictiveSectionsSelection: initState.fictiveSectionsSelection,
      };
    }
    case CLEAR_ALL_SELECTED_SECTIONS: {
      return {
        ...state,
        groupSelectionType: initState.groupSelectionType,
        shieldsSelection: initState.shieldsSelection,
        sectionsSelection: initState.sectionsSelection,
        fictiveSectionsSelection: initState.fictiveSectionsSelection,
        cmmeStates: { ...state.cmmeStates, pages: initState.cmmeStates.pages },
      };
    }
    case CLEAR_PAGES_SELECTION:
      return { ...state, pagesSelection: initState.pagesSelection };
    case ADD_SECTION: {
      const sectionToAppend = action.payload;
      const { page } = getAllLayers(sectionToAppend.id, state.pages);
      return {
        ...state,
        pages: {
          ...state.pages,
          [page.uid]: {
            ...page,
            sections: {
              ...page.sections,
              [sectionToAppend.id]: sectionToAppend,
            },
          },
        },
        isUndoRedoStateChanged: true,
      };
    }
    case ADD_SECTIONS: {
      return { ...state, pages: { ...state.pages, ...action.payload } };
    }
    case ADD_NEW_SECTIONS: {
      const {
        page,
        sectionsSelection,
        shieldsSelection = state.shieldsSelection,
        isUndoRedoStateChanged = state.isUndoRedoStateChanged,
      } = action.payload;
      return {
        ...state,
        pages: { ...state.pages, [page.uid]: { ...page } },
        shieldsSelection,
        sectionsSelection,
        isUndoRedoStateChanged,
      };
    }
    case SET_USER_SIZE_UNIT:
      return { ...state, userSizeUnit: action.payload };
    case ADD_PAGE_ROTATION: {
      const { pagesSelection, pages } = state;
      if (pagesSelection.length && pages[pagesSelection[0]]) {
        const changePage = { ...pages[pagesSelection[0]] };
        let newAngle = changePage.transforms.angle + action.payload;
        if (newAngle >= 360) {
          newAngle = newAngle - 360;
        } else if (newAngle < 0) {
          newAngle = newAngle + 360;
        }
        changePage.transforms = {
          ...changePage.transforms,
          angle: newAngle,
        };
        return {
          ...state,
          pages: {
            ...pages,
            [changePage.uid]: changePage,
          },
          isUndoRedoStateChanged: true,
        };
      } else {
        return state;
      }
    }
    case ADD_PAGE_MIRROR_X: {
      const { pagesSelection, pages } = state;
      if (pagesSelection.length && pages[pagesSelection[0]]) {
        const changePage = { ...pages[pagesSelection[0]] };
        changePage.transforms = {
          ...changePage.transforms,
          mirror_x: !changePage.transforms.mirror_x,
        };
        return {
          ...state,
          pages: {
            ...pages,
            [changePage.uid]: changePage,
          },
          isUndoRedoStateChanged: true,
        };
      } else {
        return state;
      }
    }
    case ADD_PAGE_MIRROR_Y: {
      const { pagesSelection, pages } = state;
      if (pagesSelection.length && pages[pagesSelection[0]]) {
        const changePage = { ...pages[pagesSelection[0]] };
        changePage.transforms = {
          ...changePage.transforms,
          mirror_y: !changePage.transforms.mirror_y,
        };
        return {
          ...state,
          pages: {
            ...pages,
            [changePage.uid]: changePage,
          },
          isUndoRedoStateChanged: true,
        };
      } else {
        return state;
      }
    }
    case SET_SNACKBAR_STATE: {
      return { ...state, isShowSnackbar: action.payload };
    }
    case SET_USER_DEVICE_MANIPULATOR_TYPE:
      return { ...state, userDeviceManipulatorType: action.payload };
    case SET_OTHER_CONTENT_STATE:
      return { ...state, includeOtherContent: action.payload };
    case SET_AUTOFILL_AND_COPPY_PATTERN:
      return { ...state, autoFillAndCopyPattern: action.payload };
    case SET_FICTIVE_SECTIONS:
      const currentPage = action.payload?.page;
      const fictiveSections = action.payload?.fictiveSections;
      const fictiveSectionsSelection = action.payload?.fictiveSectionsSelection;
      const sectionsSelection = action.payload?.sectionsSelection;

      if (currentPage?.sections) {
        return {
          ...state,
          fictiveSections: fictiveSections,
          fictiveSectionsSelection: fictiveSectionsSelection,
          sectionsSelection: sectionsSelection,
          pages: {
            ...state.pages,
            [currentPage.uid]: {
              ...currentPage,
              sections: {
                ...currentPage.sections,
              },
            },
          },
        };
      } else {
        return {
          ...state,
          fictiveSections: fictiveSections,
        };
      }
    case ADD_SECTION_TO_MATRIX_SELECTION: {
      const sectionToSelect = action.payload;
      const { page, shield } = sectionToSelect
        ? getAllLayers(sectionToSelect.id, state.pages)
        : null;
      const sectionsSelection = state.sectionsSelection || {};
      // move selected section to the end of list to render on svg over all other sections
      if (
        page?.sections &&
        sectionToSelect?.id &&
        page.sections[sectionToSelect.id]
      ) {
        if (sectionsSelection && Object.keys(sectionsSelection)?.length > 1) {
          cmmeStatePages[page.uid] = {
            ...cmmeStatePages[page.uid],
            selectedSection: undefined,
            shieldsSelection: state.shieldsSelection,
            sectionsSelection: sectionsSelection,
          };
        } else if (sectionToSelect?.id) {
          cmmeStatePages[page.uid] = {
            ...cmmeStatePages[page.uid],
            selectedSection: sectionToSelect.id,
            shieldsSelection: state.shieldsSelection,
            sectionsSelection: {},
          };
        }
        cmmeStates['pages'] = cmmeStatePages;

        return {
          ...state,
          cmmeStates: cmmeStates,
          fictiveSectionsSelection: {
            ...sectionsSelection,
            [sectionToSelect.id]: sectionToSelect,
          },
          sectionsSelection: {
            ...sectionsSelection,
            [sectionToSelect.id]: sectionToSelect,
          },
          shieldsSelection: { [shield.id]: shield },
          pages: { ...state.pages, [page.uid]: { ...page } },
        };
      } else {
        return {
          ...state,
          fictiveSectionsSelection: {
            ...sectionsSelection,
          },
          sectionsSelection: {
            ...sectionsSelection,
          },
          pages: {
            ...state.pages,
          },
        };
      }
    }
    case REMOVE_SECTION_FROM_MATRIX_SELECTION: {
      const sectionToSelect = action.payload;
      const { page } = getAllLayers(sectionToSelect.id, state.pages);
      // move selected section to the end of list to render on svg over all other sections
      const selSec = page.sections[sectionToSelect.id];
      delete page.sections[sectionToSelect.id];
      const sectionsSelection = state.sectionsSelection || [];
      if (sectionsSelection[sectionToSelect.id]) {
        delete sectionsSelection[sectionToSelect.id];
      }
      page.sections[selSec.id] = selSec;
      cmmeStatePages[page.uid] = {
        ...cmmeStatePages[page.uid],
        selectedSection: undefined,
        shieldsSelection: state.shieldsSelection,
        sectionsSelection: sectionsSelection,
      };
      cmmeStates['pages'] = cmmeStatePages;
      return {
        ...state,
        cmmeStates: cmmeStates,
        sectionsSelection: sectionsSelection,
        fictiveSectionsSelection: sectionsSelection,
        shieldsSelection: {},
        pages: { ...state.pages, [page.uid]: { ...page } },
      };
    }
    case SET_AUTOFILL_MATRIX_TEMPLATE: {
      const {
        autoFillMatrixTemplate,
        autoFillMatrixStyleTemplate,
        fillMode,
      } = action.payload;
      return {
        ...state,
        autoFillMatrixTemplate,
        autoFillMatrixStyleTemplate,
        fillMode,
      };
    }
    case SET_LASER_OUTPUT_QUALITY: {
      return {
        ...state,
        laserOutputQuality: action.payload,
        isUndoRedoStateChanged: true,
      };
    }
    case SET_PLATFORM_ENVIRONMENT: {
      return {
        ...state,
        platform: { config: { environment: action.payload } },
      };
    }
    case SET_FONT_SIZE_COEF: {
      return { ...state, fontSizeCoef: action.payload };
    }
    case SET_LINE_HEIGHT_COEF: {
      return { ...state, lineHeightCoef: action.payload };
    }
    case SET_LETTER_SPACING_COEF: {
      return { ...state, letterSpacingCoef: action.payload };
    }
    case SET_VARIANT_COLOR_MODAL_OPEN: {
      return { ...state, isVariantColorModalOpen: action.payload };
    }
    case SET_FLOATABLE_INPUT_VISIBLE: {
      return { ...state, floatableInputVisible: action.payload };
    }
    case SET_PRINTING: {
      return { ...state, isPrinting: action.payload };
    }
    case ADD_SELECTED_SECTIONS: {
      const {
        sectionsSelection,
        shieldsSelection,
        fictiveSectionsSelection = {},
        floatableInputVisible = state.floatableInputVisible,
      } = action.payload;
      return {
        ...state,
        sectionsSelection,
        shieldsSelection,
        fictiveSectionsSelection,
        floatableInputVisible,
      };
    }
    case SPLIT_SECTIONS: {
      const {
        page,
        sectionsSelection,
        shieldsSelection = state.shieldsSelection,
      } = action.payload;

      return {
        ...state,
        pages: { ...state.pages, [page.uid]: { ...page } },
        sectionsSelection,
        shieldsSelection,
        isUndoRedoStateChanged: true,
      };
    }
    default: {
      return envReducer(state, action) || state;
    }
  }
}
